import { isChunkedDocumentContentRoot, isTextNode } from '../../typeValidators';
import type { RangyRange } from '../types/rangy';
import { getFirstNodeDescendantWith, getLastNodeDescendantWith } from './getDescendantWith';
import getRangeFromNodes from './getRangeFromNodes';
import rangy from './rangy';

/*
  Range spanning the actually-highlightable content within a content root.

  A chunked document's content root holds its real content in `.rw-chunk-container` elements plus a
  trailing #end-of-content resize sentinel that lives outside every chunk. The sentinel can't be
  serialized (the chunked serializer requires positions to resolve to a chunk container), so a
  selection dragged past the last paragraph would otherwise fail to become a highlight. Spanning the
  chunk content lets callers crop such selections back to the content. Non-chunked roots fall back
  to the whole container.

  The boundary is anchored at the chunk content's true edges — the deepest first/last descendants
  (text or an element like an image) — rather than the chunk elements' child-list edges. A position
  such as (chunk, childNodes.length) serializes to an index the chunked deserializer can't resolve.
*/
export default function getHighlightableContentRange(containerNode: Node): RangyRange {
  if (isChunkedDocumentContentRoot(containerNode)) {
    // Unloaded (virtualized) chunks stay in the DOM but are emptied, so anchor to the first/last
    // chunk that actually has content. The selection only ever covers loaded chunks, and anchoring
    // to an empty boundary chunk would yield no descendant and fall through to the whole container,
    // re-including the #end-of-content sentinel (the bug this helper exists to prevent).
    const loadedChunks = Array.from(
      containerNode.querySelectorAll<HTMLElement>('.rw-chunk-container'),
    ).filter((chunk) => chunk.hasChildNodes());
    const firstChunk = loadedChunks[0];
    const lastChunk = loadedChunks[loadedChunks.length - 1];
    const start = firstChunk && getFirstNodeDescendantWith(firstChunk, () => true);
    const end = lastChunk && getLastNodeDescendantWith(lastChunk, () => true);

    if (start && end) {
      const range = rangy.createRange(containerNode.ownerDocument ?? undefined);

      if (isTextNode(start)) {
        range.setStart(start, 0);
      } else {
        range.setStartBefore(start);
      }

      if (isTextNode(end)) {
        range.setEnd(end, end.length);
      } else {
        range.setEndAfter(end);
      }

      return range;
    }
  }
  return getRangeFromNodes([containerNode]);
}
